import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";

// DRAFT terms of service. Fill [bracketed] placeholders and review
// before launch. The AI-recommendations and limitation-of-liability
// clauses matter most for a product that suggests real places and earns
// affiliate commissions; get them reviewed.

const LAST_UPDATED = "May 2026";

const Terms = () => {
  return (
    <div className="bg-paper-deep"><div className="mx-auto max-w-[760px] px-5 py-12"><div className="bg-paper border border-rule p-6 sm:p-10">
      <Link
        to="/"
        className="kicker inline-flex items-center gap-2 min-h-[44px] text-ink-muted hover:text-ink transition-colors mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to SundayAtlas
      </Link>

      <div className="flex items-start gap-2.5 border border-rule bg-paper-warm p-3.5 mb-9">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-accent-text" />
        <span className="font-serif text-sm text-ink-muted leading-relaxed">
          Draft. Replace every [bracketed] placeholder and have this reviewed
          before launch. Remove this banner when finalized.
        </span>
      </div>

      <h1 className="font-serif text-4xl text-ink">Terms of Service</h1>
      <p className="kicker text-ink-faint mt-3 mb-9">
        Last updated: {LAST_UPDATED}
      </p>

      <div className="space-y-6 font-serif text-[15px] leading-relaxed text-ink [&_h2]:kicker [&_h2]:text-accent-text [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:pt-5 [&_h2]:border-t [&_h2]:border-rule-soft [&_a]:text-accent-text [&_a]:underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_p]:text-ink-muted [&_li]:text-ink-muted [&_strong]:text-ink [&_strong]:font-semibold">
        <p>
          These terms govern your use of SundayAtlas, operated by [your legal
          entity / name] ("we", "us"). By using the app you agree to them.
        </p>

        <h2>1. What SundayAtlas is</h2>
        <p>
          SundayAtlas is an AI-assisted travel planning tool. It helps you build
          day-by-day itineraries, surfaces creator content, and links out to
          booking and reference sites. It is a planning aid, not a travel
          agency, and it does not sell travel products itself.
        </p>

        <h2>2. AI recommendations: verify before you rely on them</h2>
        <p>
          Itineraries, place details, opening hours, prices, and other
          information are generated by AI and third-party data sources. They can
          be inaccurate, outdated, or incomplete. You are responsible for
          verifying anything important (especially opening hours, prices,
          availability, visa and entry requirements, and safety) directly with
          the venue or an official source before you travel or book. We are not
          liable for losses arising from reliance on AI-generated content.
        </p>

        <h2>3. Bookings and affiliate links</h2>
        <p>
          Booking and "official site" links take you to third-party providers
          (e.g., Booking.com). Any booking is a contract between you and that
          provider, governed by their terms. We may earn a commission when you
          book through these links, at no extra cost to you. We don't control
          those sites and aren't responsible for their content, pricing, or
          service.
        </p>

        <h2>4. Acceptable use</h2>
        <ul>
          <li>Don't abuse, overload, scrape, or attempt to break the service.</li>
          <li>Don't use it for unlawful purposes.</li>
          <li>
            Don't attempt to extract our prompts, reverse-engineer the service,
            or resell it without permission.
          </li>
        </ul>

        <h2>5. Your content</h2>
        <p>
          Trips you create are yours. They are stored on your device, and also
          in your account if you sign in. When you create a share link, you
          grant us permission to store and display that trip so the link works.
          You're responsible for the content you enter.
        </p>

        <h2>6. Third-party services</h2>
        <p>
          The app relies on Anthropic, Google, Vercel, Upstash, and others. Your
          use of features powered by them is also subject to their terms. See
          our <Link to="/privacy">Privacy Policy</Link> for the full list.
        </p>

        <h2>7. No warranty</h2>
        <p>
          The service is provided "as is" and "as available", without warranties
          of any kind, to the fullest extent permitted by law. We don't warrant
          that it will be uninterrupted, error-free, or that recommendations
          will suit your needs.
        </p>

        <h2>8. Limitation of liability</h2>
        <p>
          To the fullest extent permitted by law, we are not liable for indirect,
          incidental, or consequential damages, or for losses arising from your
          use of the service, AI-generated content, or third-party sites. Some
          jurisdictions don't allow these limitations, in which case they apply
          to the extent permitted.
        </p>

        <h2>9. Changes</h2>
        <p>
          We may update these terms; the "last updated" date reflects the latest
          version. Continued use after changes means you accept them.
        </p>

        <h2>10. Governing law</h2>
        <p>
          These terms are governed by the laws of [your jurisdiction, e.g.,
          Austria], without regard to conflict-of-law rules.
        </p>

        <h2>11. Contact</h2>
        <p>
          [your legal entity / name].{" "}
          <a href="mailto:[your-email@example.com]">[your-email@example.com]</a>.
        </p>
      </div>
    </div></div></div>
  );
};

export default Terms;
